import { openDB, IDBPDatabase } from 'idb';
import { Language, TranslationMode, PriceOverride } from '../types';

export interface PendingQuote {
  files: File[];
  sourceLanguage: Language;
  targetLanguage: Language;
  translationMode: TranslationMode;
  price_override?: PriceOverride;
  expiresAt: number;
}

const DB_NAME = 'translator_app';
const STORE_NAME = 'pending_quotes';
const KEY = 'pending_quote';
const TTL_MS = 60 * 60 * 1000; // 1 hour

async function getDB(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    },
  });
}

export async function savePendingQuote(quote: Omit<PendingQuote, 'expiresAt'>): Promise<void> {
  const db = await getDB();
  const entry: PendingQuote = { ...quote, expiresAt: Date.now() + TTL_MS };
  await db.put(STORE_NAME, entry, KEY);
}

export async function readAndDeletePendingQuote(): Promise<PendingQuote | null> {
  const db = await getDB();
  // Use a single readwrite transaction so concurrent calls can't both read
  // before either deletes (fixes React StrictMode double-invoke race condition)
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const entry = await tx.store.get(KEY) as PendingQuote | undefined;
  if (!entry) {
    await tx.done;
    return null;
  }
  await tx.store.delete(KEY);
  await tx.done;
  if (Date.now() > entry.expiresAt) return null; // expired
  return entry;
}

export async function cleanupExpiredQuote(): Promise<void> {
  const db = await getDB();
  const entry = await db.get(STORE_NAME, KEY) as PendingQuote | undefined;
  if (entry && Date.now() > entry.expiresAt) {
    await db.delete(STORE_NAME, KEY);
  }
}
